import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import {
  DailySubscriberRow,
  EntryPointBreakdownRow,
} from "@redotech/redo-marketing-analytics/types";
import { AudienceSubscriptionTypeFilter } from "@redotech/redo-marketing-common/audience-subscription-type";
import {
  MarketingEntryPoint,
  marketingEntryPointPrettyNames,
} from "@redotech/redo-marketing-entry-point-common/marketing-entry-point";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { RedoMixedBarAndLineChart } from "@redotech/redo-web/arbiter-components/charts/line-and-bar-chart/redo-mixed-bar-and-line-chart";
import {
  DataPoint,
  MixedChartSeries,
} from "@redotech/redo-web/arbiter-components/charts/line-and-bar-chart/types";
import { RedoPieChart } from "@redotech/redo-web/arbiter-components/charts/pie-chart/redo-pie-chart";
import { RedoMetricItem } from "@redotech/redo-web/arbiter-components/metric-item/redo-metric-item";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { DATA_VISUALIZATION_COLORS } from "@redotech/redo-web/theme/colors";
import { memo, useMemo } from "react";
import { analyticsDateRange } from "../analytics-date-range";
import { ChannelFilter } from "../filters/marketing-channel-filter";

export const MarketingAnalyticsAudienceGrowth = memo(
  function MarketingAnalyticsAudienceGrowth({
    dateFilter,
    channelFilter,
  }: {
    dateFilter: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
    channelFilter: ChannelFilter;
  }) {
    // Map channelFilter to subscriptionType
    const subscriptionType: AudienceSubscriptionTypeFilter = useMemo(() => {
      if (channelFilter === "all") {
        return "both";
      }
      if (channelFilter === MarketingChannel.EMAIL) {
        return "email";
      }
      if (channelFilter === MarketingChannel.SMS) {
        return "sms";
      }
      return "both";
    }, [channelFilter]);

    const { startDate, endDate } = analyticsDateRange(dateFilter);

    const {
      data: audienceGrowthResult,
      isLoading,
      error: failedToLoad,
    } = useRpcQuery(
      RedoMarketingRpcClientContext,
      "getAudienceGrowthAnalytics",
      { startDate, endDate, subscriptionType },
    );

    const calculatePercentageChange = (current: number, previous: number) => {
      if (previous === 0 && current === 0) {
        return 0;
      }
      if (previous === 0) {
        return null;
      }

      const change = ((current - previous) / previous) * 100;

      // Cap extremely large percentage changes for display purposes
      if (change > 999) {
        return 999;
      }
      if (change < -999) {
        return -999;
      }

      return change;
    };

    const chartSeries = useMemo((): MixedChartSeries[] => {
      if (!audienceGrowthResult?.dailySubscribers) {
        return [];
      }

      const series: MixedChartSeries[] = [];

      if (subscriptionType === "both" || subscriptionType === "email") {
        const emailData: DataPoint[] =
          audienceGrowthResult.dailySubscribers.map(
            (day: DailySubscriberRow) => ({
              date: Temporal.Instant.from(day.DATE.toISOString()),
              value: day.EMAIL_SUBSCRIBERS,
            }),
          );

        series.push({
          name: "Email",
          data: emailData,
          color: DATA_VISUALIZATION_COLORS.cyan[500],
          type: "bar" as const,
        });
      }

      if (subscriptionType === "both" || subscriptionType === "sms") {
        const smsData: DataPoint[] = audienceGrowthResult.dailySubscribers.map(
          (day: DailySubscriberRow) => ({
            date: Temporal.Instant.from(day.DATE.toISOString()),
            value: day.SMS_SUBSCRIBERS,
          }),
        );

        series.push({
          name: "SMS",
          data: smsData,
          color: DATA_VISUALIZATION_COLORS.cyan[200],
          type: "bar" as const,
        });
      }

      return series;
    }, [audienceGrowthResult?.dailySubscribers, subscriptionType]);

    const confirmationChartSeries = useMemo((): MixedChartSeries[] => {
      if (!audienceGrowthResult?.dailySubscribers) {
        return [];
      }

      const signUpData: DataPoint[] = audienceGrowthResult.dailySubscribers.map(
        (day: DailySubscriberRow) => ({
          date: Temporal.Instant.from(day.DATE.toISOString()),
          value: day.EMAIL_SUBSCRIBERS + day.SMS_SUBSCRIBERS,
        }),
      );

      const confirmationData: DataPoint[] =
        audienceGrowthResult.dailySubscribers.map(
          (day: DailySubscriberRow) => ({
            date: Temporal.Instant.from(day.DATE.toISOString()),
            value: day.EMAIL_CONFIRMATIONS + day.SMS_CONFIRMATIONS,
          }),
        );

      return [
        {
          name: "Sign ups",
          data: signUpData,
          color: DATA_VISUALIZATION_COLORS.cyan[500],
          type: "line" as const,
        },
        {
          name: "Confirmations",
          data: confirmationData,
          color: "#333333",
          type: "line" as const,
        },
      ];
    }, [audienceGrowthResult?.dailySubscribers]);

    const pieChartSeries = useMemo(() => {
      if (!audienceGrowthResult?.entryPointBreakdown) {
        return [];
      }
      return audienceGrowthResult.entryPointBreakdown.map(
        (entry: EntryPointBreakdownRow) => ({
          label:
            marketingEntryPointPrettyNames[
              entry.ENTRY_POINT as MarketingEntryPoint
            ] || entry.ENTRY_POINT,
          value: entry.COUNT,
        }),
      );
    }, [audienceGrowthResult?.entryPointBreakdown]);

    const totals = audienceGrowthResult?.totals;
    const newSubscribersChange = totals
      ? calculatePercentageChange(
          totals.newSubscribers,
          totals.previousNewSubscribers,
        )
      : 0;
    const unsubscribesChange = totals
      ? calculatePercentageChange(
          totals.unsubscribes,
          totals.previousUnsubscribes,
        )
      : 0;
    const complaintsChange = totals
      ? calculatePercentageChange(
          totals.spamComplaints,
          totals.previousSpamComplaints,
        )
      : 0;

    if (failedToLoad) {
      return (
        <Flex dir="column" gap="4xl" w="full">
          <Text fontSize="md" fontWeight="semibold">
            Audience Growth
          </Text>
          <Text textColor="error">Error loading audience growth data</Text>
        </Flex>
      );
    }

    return (
      <Flex dir="column" gap="xl" w="full">
        <Text fontSize="md" fontWeight="semibold">
          Audience Growth
        </Text>

        <Flex dir="row" gap="4xl" w="full">
          <RedoMetricItem
            pending={isLoading}
            title="New marketing subscribers"
            value={totals?.newSubscribers?.toLocaleString() ?? "0"}
            {...(newSubscribersChange !== null && {
              comparisonChange: newSubscribersChange,
              comparisonText: "vs previous period",
              isGoodChange: newSubscribersChange >= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Unsubscribes"
            value={totals?.unsubscribes?.toLocaleString() ?? "0"}
            {...(unsubscribesChange !== null && {
              comparisonChange: unsubscribesChange,
              comparisonText: "vs previous period",
              isGoodChange: unsubscribesChange <= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Marked as spam"
            value={totals?.spamComplaints?.toLocaleString() ?? "0"}
            {...(complaintsChange !== null && {
              comparisonChange: complaintsChange,
              comparisonText: "vs previous period",
              isGoodChange: complaintsChange <= 0,
            })}
          />
        </Flex>

        <Card gap="xl" p="xl" w="full">
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            New marketing subscribers
          </Text>
          {isLoading ? (
            <Flex align="center" justify="center" py="8xl">
              <LoadingRedoAnimation />
            </Flex>
          ) : chartSeries.length > 0 ? (
            <RedoMixedBarAndLineChart
              legendPosition="top"
              series={chartSeries}
              showTooltipSeriesNames
              valueAxisFormatter={(value: number) => value.toString()}
              valueTooltipFormatter={(value: number) => `${value} subscribers`}
            />
          ) : (
            <Flex align="center" justify="center" py="8xl">
              <Text textColor="tertiary">
                No subscriber data found for the selected period
              </Text>
            </Flex>
          )}
        </Card>

        <Card gap="xl" p="xl" w="full">
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            Opt-in sources
          </Text>
          {isLoading ? (
            <Flex align="center" justify="center" py="8xl">
              <LoadingRedoAnimation />
            </Flex>
          ) : pieChartSeries.length > 0 ? (
            <RedoPieChart holeSize="75%" series={pieChartSeries} size="lg" />
          ) : (
            <Flex align="center" justify="center" py="8xl">
              <Text textColor="tertiary">
                No entry point data found for the selected period
              </Text>
            </Flex>
          )}
        </Card>

        <Card gap="xl" p="xl" w="full">
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            Sign ups & confirmations
          </Text>
          {isLoading ? (
            <Flex align="center" justify="center" py="8xl">
              <LoadingRedoAnimation />
            </Flex>
          ) : confirmationChartSeries.length > 0 ? (
            <RedoMixedBarAndLineChart
              legendPosition="top"
              series={confirmationChartSeries}
              showTooltipSeriesNames
              valueAxisFormatter={(value: number) => value.toString()}
              valueTooltipFormatter={(value: number) => value.toLocaleString()}
            />
          ) : (
            <Flex align="center" justify="center" py="8xl">
              <Text textColor="tertiary">
                No confirmation data found for the selected period
              </Text>
            </Flex>
          )}
        </Card>
      </Flex>
    );
  },
);
